import { AnySelection } from '@d3fc/d3fc-series';
import {
    getNearestParentElementWithClass,
    getTranslate,
    getViewportWidth,
} from 'utils/svgUtils';
import { select } from 'd3-selection';
import { AnyChart } from '@d3fc/d3fc-chart';
import sortBy from 'lodash/sortBy';

const Padding = 2;

interface TextNodeMeasurement {
    labelStartX: number;
    tickX: number;
    labelEndX: number;
}

export function ensureXAxisLabelsDoNotOverlap(
    chart: AnyChart,
    xDecorate?: (selection: AnySelection<SVGElement>) => void,
    configureTickIndexesToShow?: (
        selection: AnySelection<SVGElement>,
        tickIndexesToShow: number[]
    ) => void
) {
    chart.xDecorate((selection: AnySelection<SVGElement>) => {
        xDecorate && xDecorate(selection);

        const textSelection: AnySelection<SVGTextElement> =
            selection.select('text');

        const textDimensions = measureTextNodes(textSelection);

        const tickIndexesToShow =
            getIndexesToShowWithoutOverlap(textDimensions);

        removeOrShiftLastLabelIfIsCutOff(
            tickIndexesToShow,
            textDimensions,
            textSelection,
            getViewportWidth(selection)
        );

        configureTickIndexesToShow
            ? configureTickIndexesToShow(selection, tickIndexesToShow)
            : hideTicks(selection, tickIndexesToShow);

        hideVerticalGridLines(selection, tickIndexesToShow);
    });
}

function measureTextNodes(
    textSelection: AnySelection<SVGTextElement>
): Array<TextNodeMeasurement> {
    return textSelection.nodes().map((n: SVGTextElement) => {
        // Gets the X from the translate on the parent group node...
        const tickX = getTranslate(n.parentNode as SVGElement)!.x;
        const labelWidth = n.getBBox().width;
        const labelStartX = tickX - labelWidth / 2;
        const labelEndX = tickX + labelWidth / 2;
        return { labelStartX, tickX, labelEndX, text: n.textContent };
    });
}

function getIndexesToShowWithoutOverlap(
    textNodeDimensions: TextNodeMeasurement[]
): number[] {
    const sorted = sortBy(textNodeDimensions, 'tickX');
    let i = 0;
    const ret: number[] = [textNodeDimensions.indexOf(sorted[0])];

    while (i < sorted.length) {
        const current = sorted[i];
        let nextI = i + 1;

        while (
            nextI < sorted.length &&
            current.labelEndX + Padding >= sorted[nextI].labelStartX
        ) {
            nextI++;
        }
        if (nextI < sorted.length) {
            ret.push(textNodeDimensions.indexOf(sorted[nextI]));
        }
        i = nextI;
    }

    return ret;
}

function removeOrShiftLastLabelIfIsCutOff(
    tickIndexesToShow: number[],
    textNodeDimensions: TextNodeMeasurement[],
    textSelection: AnySelection<SVGTextElement>,
    chartWidth: number | undefined
) {
    if (!chartWidth) {
        return;
    }
    const textNodes = textSelection.nodes();
    const lastShownTickIndex = tickIndexesToShow[tickIndexesToShow.length - 1];
    const lastShownTickDimensions = textNodeDimensions[lastShownTickIndex];
    const beforeLastShownTickDimensions =
        textNodeDimensions[tickIndexesToShow[tickIndexesToShow.length - 2]];

    if (
        lastShownTickDimensions &&
        lastShownTickDimensions.labelEndX <= chartWidth
    ) {
        // It fits...
        return;
    }

    const lastShownLabelWidth =
        lastShownTickDimensions.labelEndX - lastShownTickDimensions.labelStartX;
    // Last element label is cut off.
    const desiredX = chartWidth - lastShownLabelWidth;
    const allowedX =
        tickIndexesToShow.length === 1
            ? 0
            : beforeLastShownTickDimensions.labelEndX + Padding;
    if (desiredX > allowedX) {
        // Shift it left
        textSelection
            .filter((_d: unknown, i: number) => i === lastShownTickIndex)
            .attr('transform', (_d: unknown, index: number) => {
                const existing = getTranslate(textNodes[index])!;
                const xAdj =
                    (lastShownTickDimensions.labelEndX - chartWidth) * -1;
                return `translate(${existing.x + xAdj},${existing?.y})`;
            });
    } else {
        // Remove it
        tickIndexesToShow.splice(tickIndexesToShow.length - 1, 1);
    }
}

function hideTicks(
    selection: AnySelection<SVGElement>,
    tickIndexesToShow: number[]
) {
    selection.attr('class', (_d: unknown, i: number) =>
        tickIndexesToShow.includes(i) ? 'tick' : 'tick hide '
    );
}

function hideVerticalGridLines(
    selection: AnySelection<SVGElement>,
    tickIndexesToShow: number[]
) {
    const chartNode = getNearestParentElementWithClass(
        selection,
        'cartesian-chart'
    );
    const chartSelection = select(chartNode);
    chartSelection
        .select('.plot-area')
        .select('svg')
        .selectAll('.gridline-y')
        .attr('class', (_d: unknown, i: number) =>
            tickIndexesToShow.includes(i) ? 'gridline-y' : 'gridline-y hide'
        );
}
